import { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import _ from "lodash";

import { useSelector, useDispatch } from "react-redux";

import ActionsStandardLabel from "./ActionsStandardLabel";
import ComparisonLegend from "../TTest/ComparisonLegend";
import SampleSizeWarning from "../SampleSizeWarning";
import { loadSampleSizeWarningFlag } from "store/features/studyDataSlice";
import CohensDLegends from "./CohensDLegends";

function AnalysisTableBottomLabel({ actionStandard = {}, tTest = {}, analysisView = {}, cohensDLegendShown = false, pageContext = "" }) {
    const dispatch = useDispatch();
    const { actionStandardKpis, actionStandardData, showActionStandardsInfo = false } = actionStandard;
    const { isFisher, tTestResult, tTestLegends, testLabel } = tTest;
    const { moduleName, isTableShown = true } = analysisView;
    const legendModuleName = pageContext || moduleName;
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isBeautyAndWellbeing = isHairCare || isSkinCare;
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const defaultTitle = selectedStudy.length > 0 ? selectedStudy[0].study_type_method__default_title?.replace(/\s+/g, "")?.toLowerCase() : "";

    const getJustifyContent = () => {
        if (moduleName === "fragranceMetrics" && testLabel && !_.isEmpty(tTest)) {
            return "space-between";
        } else if (moduleName !== "fragranceMetrics" && testLabel && !_.isEmpty(tTest)) {
            return "flex-start";
        } else {
            return "flex-end";
        }
    };

    const MODULE_RESULT_KEY_MAP = {
        overall: "overallAnalysisResult",
        agreement: "agreementAnalysisResult",
        claims: "claimsAnalysisResult",
        justRight: "justRightAnalysisResult",
        fragranceMetrics: "fragranceMetricsAnalysisResult",
        preference: "preferenceAnalysisResult",
        secondary: "secondaryAnalysisResult",
        productPreference: "productPreferenceAnalysisResult",
        fragranceAssociation: "fragranceAssociationResult",
    };

    // ✅ pick correct analysis result dynamically based on moduleName
    const analysisResult = useSelector((state) => {
        const key = MODULE_RESULT_KEY_MAP[moduleName];
        return key ? state.analysisResultsStore?.[key] || [] : [];
    });
    // compute once (memoized)
    const hasLowSampleSize = useMemo(() => {
        if (moduleName === "productPreference") {
            return analysisResult?.some(
                (group) =>
                    Number(group?.scores?.[0]?.sampleSize) > 0 &&
                    Number(group?.scores?.[0]?.sampleSize) < 75
            );
        } else {
            return analysisResult?.some(
                (p) => Number(p?.sampleSize) > 0 && Number(p?.sampleSize) < 75
            );
        }
    }, [analysisResult, moduleName]);

    //  dispatch only when value changes
    useEffect(() => {
        dispatch(loadSampleSizeWarningFlag(hasLowSampleSize));
    }, [dispatch, hasLowSampleSize]);

    return (
        <>
            <div
                style={{
                    display: "flex",
                    justifyContent: getJustifyContent(),
                    flexDirection: "column",
                }}
            >
                {testLabel && !_.isEmpty(tTest) && moduleName !== "fragranceAssociation" && (
                    <p
                        style={{
                            marginBottom: "6px",
                            color: "#495057",
                            fontWeight: 600,
                            fontSize: 12,
                        }}
                    >
                        {testLabel}
                    </p>
                )}
                {hasLowSampleSize && !isBeautyAndWellbeing && (
                    <SampleSizeWarning
                        threshold={75}
                        color="#f44336"
                    />
                )}
            </div>

            {/* Note for Fisher's LSD and Post-hoc Dunn Test only for skin and hair care*/}
            {isBeautyAndWellbeing && tTestResult?.length !== 0 && (
                (testLabel?.toLowerCase().includes("fisher") || testLabel?.toLowerCase().includes("post-hoc dunn")) && (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginTop: "0px",
                            marginBottom: "12px",
                            flexDirection: "column",
                            alignItems: "flex-end",
                        }}
                    >
                        <p
                            style={{
                                margin: 0,
                                marginBottom: "4px",
                                color: "#495057",
                                fontWeight: 600,
                                fontSize: 12,
                                textAlign: "right",
                            }}
                        >
                            Note: Matching letters indicate that there is no statistically significant difference between products,
                        </p>
                        <p
                            style={{
                                margin: 0,
                                color: "#495057",
                                fontWeight: 600,
                                fontSize: 12,
                                textAlign: "right",
                            }}
                        >
                            while different letters indicate a statistically significant difference.
                        </p>
                    </div>
                ))
            }

            {showActionStandardsInfo && !_.isEmpty(actionStandard) && (
                <ActionsStandardLabel
                    actionStandardKpis={actionStandardKpis}
                    actionStandardData={actionStandardData}
                    moduleName={moduleName}
                    defaultTitle={defaultTitle}
                />
            )}
            {cohensDLegendShown && <CohensDLegends />}
            {isTableShown && !isFisher && tTestResult?.length !== 0 && tTestLegends?.length === 2 && moduleName !== "fragranceAssociation" && (
                <ComparisonLegend
                    legends={tTestLegends}
                    moduleName={legendModuleName}
                />
            )}
        </>
    );
}

AnalysisTableBottomLabel.propTypes = {
    actionStandard: PropTypes.object,
    tTest: PropTypes.object,
    analysisView: PropTypes.object,
    cohensDLegendShown: PropTypes.bool,
    pageContext: PropTypes.string,
};

export default AnalysisTableBottomLabel;
